import { useState, useEffect, useCallback } from 'react';
import { notificationService, NotificationPermissionStatus, NotificationOptions } from '../services/notification';

export interface UseNotificationReturn {
  permission: NotificationPermissionStatus;
  isSupported: boolean;
  requestPermission: () => Promise<NotificationPermissionStatus>;
  sendNotification: (options: NotificationOptions) => Promise<void>;
  sendProgramReminder: (programName: string) => Promise<void>;
  sendGeneralNotification: (title: string, body: string) => Promise<void>;
  clearAllNotifications: () => Promise<void>;
  clearNotificationsByTag: (tag: string) => Promise<void>;
}

/**
 * React hook for managing notifications in PWA
 */
export function useNotification(): UseNotificationReturn {
  const [permission, setPermission] = useState<NotificationPermissionStatus>(
    notificationService.getPermissionStatus()
  );
  const [isSupported] = useState(notificationService.isSupported());

  useEffect(() => {
    // Update permission status when it changes
    const updatePermissionStatus = () => {
      const nextPermission = notificationService.getPermissionStatus();
      setPermission(nextPermission);

      if (nextPermission === 'granted') {
        notificationService.syncPushRegistration({ force: true }).catch(console.error);
      }
    };

    // Check permission status on mount
    updatePermissionStatus();

    // Listen for permission changes (if supported)
    let permissionStatus: PermissionStatus | null = null;

    if ('permissions' in navigator) {
      navigator.permissions
        .query({ name: 'notifications' as PermissionName })
        .then((status) => {
          permissionStatus = status;
          status.addEventListener('change', updatePermissionStatus);
        })
        .catch(console.error);
    }

    return () => {
      permissionStatus?.removeEventListener('change', updatePermissionStatus);
    };
  }, []);

  const requestPermission = useCallback(async (): Promise<NotificationPermissionStatus> => {
    const newPermission = await notificationService.requestPermission();
    setPermission(newPermission);
    return newPermission;
  }, []);

  const sendNotification = useCallback(async (options: NotificationOptions): Promise<void> => {
    await notificationService.sendNotification(options);
  }, []);

  const sendProgramReminder = useCallback(async (programName: string): Promise<void> => {
    await notificationService.sendProgramReminder(programName);
  }, []);

  const sendGeneralNotification = useCallback(async (title: string, body: string): Promise<void> => {
    await notificationService.sendGeneralNotification(title, body);
  }, []);

  const clearAllNotifications = useCallback(async (): Promise<void> => {
    await notificationService.clearAllNotifications();
  }, []);

  const clearNotificationsByTag = useCallback(async (tag: string): Promise<void> => {
    await notificationService.clearNotificationsByTag(tag);
  }, []);

  return {
    permission,
    isSupported,
    requestPermission,
    sendNotification,
    sendProgramReminder,
    sendGeneralNotification,
    clearAllNotifications,
    clearNotificationsByTag,
  };
}
